import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Logo } from "@/components/common";
import { HeaderNavigation } from "./HeaderNavigation";
import { QuickSearch } from "./QuickSearch";
import NotificationsBell from "./NotificationsBell";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { User, LogOut, Settings, Briefcase, Shield, LayoutDashboard, Menu, Sparkles } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { mainNavigation } from "@/data/navigation";

// Mobile-responsive header fix
const Header = () => {
  const { user, isAuthenticated, logout, hasRole } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getUserDisplayName = () => {
    if (!user) return 'User';

    // Try different name fields in order of preference
    if (user.name) return user.name;
    if (user.preferred_username) return user.preferred_username;

    // Fallback to email username part
    const emailUsername = user.email?.split('@')[0] || 'User';
    return emailUsername.charAt(0).toUpperCase() + emailUsername.slice(1);
  };

  const getUserInitials = () => {
    const displayName = getUserDisplayName();
    const names = displayName.trim().split(' ');
    if (names.length >= 2) {
      return names[0].charAt(0) + names[names.length - 1].charAt(0);
    }
    return displayName.substring(0, 2).toUpperCase();
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-[#083344] text-white font-bold border-b pt-[env(safe-area-inset-top)]">
      <div className="flex h-16 items-center justify-between px-3 sm:px-4 max-w-[1400px] mx-auto w-full">
        <div className="flex items-center gap-2 sm:gap-4 lg:gap-8">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden text-white hover:bg-white/10 shrink-0">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-[#083344] text-white border-white/10 p-0">
              <div className="flex flex-col h-full">
                <div className="p-6 border-b border-white/10">
                  <Logo size="md" variant="light" />
                </div>
                <div className="flex-1 overflow-y-auto p-6">
                  <nav className="flex flex-col gap-4">
                    {mainNavigation.map((section) => (
                      <div key={section.label} className="flex flex-col gap-2">
                        <div className="text-white/60 text-xs font-semibold uppercase tracking-wider mt-4 first:mt-0">
                          {section.label}
                        </div>
                        {section.items ? (
                          <div className="flex flex-col gap-1 ml-2">
                            {section.items.map((item) => (
                              <Link
                                key={item.href}
                                to={item.href}
                                className="text-lg font-medium py-2 hover:text-primary transition-colors flex items-center gap-2"
                              >
                                {item.icon && <span>{item.icon}</span>}
                                {item.label}
                              </Link>
                            ))}
                          </div>
                        ) : section.href?.startsWith("http") ? (
                          <a
                            href={section.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-lg font-medium py-2 hover:text-primary transition-colors flex items-center gap-2"
                          >
                            {section.icon && <span>{section.icon}</span>}
                            {section.label}
                          </a>
                        ) : (
                          <Link
                            to={section.href || "#"}
                            className="text-lg font-medium py-2 hover:text-primary transition-colors flex items-center gap-2"
                          >
                            {section.icon && <span>{section.icon}</span>}
                            {section.label}
                          </Link>
                        )}
                      </div>
                    ))}
                    <div className="mt-6 pt-6 border-t border-white/10">
                      <Link
                        to="/employers"
                        className="text-lg font-medium py-2 hover:text-primary transition-colors block"
                      >
                        For Employers
                      </Link>
                      {isAuthenticated && hasRole('CANDIDATE') && (
                        <Link
                          to="/ai-agent"
                          className="text-lg font-medium py-2 hover:text-primary transition-colors flex items-center gap-2"
                        >
                          <Sparkles className="h-4 w-4" />
                          AI Agent
                        </Link>
                      )}
                    </div>
                  </nav>
                </div>
              </div>
            </SheetContent>
          </Sheet>
          <Logo size="md" variant="light" className="lg:hidden" />
          <Logo size="lg" variant="light" className="hidden lg:flex" />
          <HeaderNavigation />
        </div>

        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
          <QuickSearch />
          {isAuthenticated && <NotificationsBell />}
          {isAuthenticated && user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-9 w-9 sm:h-10 sm:w-10 rounded-full p-0">
                  <Avatar className="h-9 w-9 sm:h-10 sm:w-10">
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="flex items-center justify-start gap-2 p-2">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{getUserDisplayName()}</p>
                    <p className="text-xs text-muted-foreground leading-none">{user.email}</p>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')} className="flex items-center gap-2">
                  <User size={16} />
                  <span>Profile</span>
                </DropdownMenuItem>
                {hasRole('EMPLOYER') && !hasRole('ADMIN') && (
                  <DropdownMenuItem onClick={() => navigate('/employer-dashboard')} className="flex items-center gap-2">
                    <LayoutDashboard size={16} />
                    <span>Employer Dashboard</span>
                  </DropdownMenuItem>
                )}
                {hasRole('EMPLOYER') && !hasRole('ADMIN') && (
                  <DropdownMenuItem onClick={() => navigate('/post-job')} className="flex items-center gap-2">
                    <Briefcase size={16} />
                    <span>Free Post a Job</span>
                  </DropdownMenuItem>
                )}
                {hasRole('CANDIDATE') && (
                  <DropdownMenuItem onClick={() => navigate('/ai-agent')} className="flex items-center gap-2">
                    <Sparkles size={16} />
                    <span>AI Agent</span>
                  </DropdownMenuItem>
                )}
                {hasRole('ADMIN') && (
                  <DropdownMenuItem onClick={() => navigate('/admin-dashboard')} className="flex items-center gap-2">
                    <Shield size={16} />
                    <span>Admin Dashboard</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate(hasRole('EMPLOYER') && !hasRole('ADMIN') ? '/employer-settings' : '/settings')} className="flex items-center gap-2">
                  <Settings size={16} />
                  <span>Settings</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2 text-red-600 focus:text-red-600">
                  <LogOut size={16} />
                  <span>Logout</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center gap-2">
              <Button onClick={() => navigate('/employers')} variant="outline" className="hidden md:flex items-center gap-2 border-secondary-foreground/30 text-secondary-foreground hover:bg-secondary-foreground/10">
                For Employer
              </Button>
              <Button
                onClick={() => navigate('/login')}
                className="flex whitespace-nowrap px-3 py-1.5 h-auto text-[11px] sm:text-sm font-bold bg-primary text-primary-foreground hover:bg-primary/90 rounded-md"
              >
                Login / Sign up
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
